import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router-dom';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { loginSchema, LoginSchema } from '@/schemas/auth/loginSchema';
import { useState } from 'react';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const { signIn, resetPassword, user, profile, loading } = useAuth();
  const { toast } = useToast();

  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: true,
    },
    mode: 'onChange',
  });

  const email = form.watch('email');

  if (user && profile && !loading) {
    return <Navigate to="/admin" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          <span className="text-gray-600">Cargando...</span>
        </div>
      </div>
    );
  }

  const onSubmit = async (values: LoginSchema) => {
    setIsLoading(true);

    if (showResetPassword) {
      if (!email) {
        toast({
          title: 'Error',
          description: 'Por favor ingresa tu email',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      const { error } = await resetPassword(email);

      if (error) {
        toast({
          title: 'Error',
          description: error.message || 'Error al enviar el email',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Email enviado',
          description: 'Revisa tu bandeja de entrada',
        });
        setShowResetPassword(false);
      }

      setIsLoading(false);
      return;
    }

    const { error } = await signIn(
      values.email,
      values.password!,
      values.rememberMe,
    );

    if (error) {
      toast({
        title: 'Error de autenticación',
        description: error.message || 'Credenciales incorrectas',
        variant: 'destructive',
      });
    } else if (values.rememberMe) {
      toast({
        title: 'Sesión iniciada',
        description: 'Tu sesión se mantendrá activa por 7 días',
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center gap-2">
            <img
              src="/lovable-uploads/icono-estancia-corta.png"
              alt="Estancia Corta"
              className="w-8 h-8 object-contain"
            />

            <h1 className="text-3xl font-bold text-gray-900">Estancia Corta</h1>
          </Link>

          <h2 className="mt-6 text-2xl font-bold text-gray-900">
            Panel de Administración
          </h2>

          <p className="mt-2 text-gray-600">
            Ingresa tus credenciales para acceder
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {showResetPassword ? 'Restablecer Contraseña' : 'Iniciar Sesión'}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="tu@email.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!showResetPassword && (
                  <>
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contraseña</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="•••••"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="rememberMe"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              id="rememberMe"
                            />
                          </FormControl>

                          <FormLabel htmlFor="rememberMe" className='!m-0'>
                            Mantener sesión iniciada por 7 días
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  </>
                )}

                <Button
                  type="submit"
                  className="w-full bg-brand-primary hover:bg-brand-primary-hover"
                  disabled={isLoading || !form.formState.isValid}
                >
                  {isLoading
                    ? 'Procesando...'
                    : showResetPassword
                    ? 'Enviar email'
                    : 'Iniciar Sesión'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="text-sm text-brand-primary hover:text-brand-primary-hover"
                  >
                    {showResetPassword
                      ? 'Volver al inicio de sesión'
                      : '¿Olvidaste tu contraseña?'}
                  </button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        <div className="text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-500">
            ← Volver al sitio web
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
